import { put, call } from 'redux-saga/effects';

import {
  getBusinessesSuccess,
  getBusinessesFailure,
  updateBusinessStatusSuccess,
  updateBusinessStatusFailure,
  getBusinessMeSuccess,
  getBusinessMeFailure,
  getBusinessRewardsSuccess,
  getBusinessRewardsFailure,
  uploadBusinessRewardsSuccess,
  uploadBusinessRewardsFailure,
  deleteBusinessRewardSuccess,
  deleteBusinessRewardFailure,
  getBusinessRedemptionsSuccess,
  getBusinessRedemptionsFailure,
  getBusinessQrSuccess,
  getBusinessQrFailure,
  // alias action creators to avoid naming conflicts with saga exports
  getBusinessesRequest as fetchBusinessesAction,
  getBusinessRewardsRequest as fetchRewardsAction,
  showAlert,
} from 'actions/index';

// Safely extract a display string from an apisauce error response.
// NestJS validation errors can return message as an array; guard against objects too.
function errMsg(data, fallback) {
  const raw = data?.error?.message;
  if (!raw) return fallback;
  if (Array.isArray(raw)) return raw.join(', ');
  if (typeof raw !== 'string') return fallback;
  return raw;
}

export function* getBusinessesRequest(api) {
  const { status, data } = yield call(api.getBusinesses);
  if (status === 200) {
    yield put(getBusinessesSuccess(data.data));
  } else {
    yield put(showAlert(errMsg(data, 'Failed to load businesses'), { variant: 'danger' }));
    yield put(getBusinessesFailure());
  }
}

export function* updateBusinessStatusRequest(api, action) {
  const { id, status: newStatus } = action.payload;
  const { status, data } = yield call(api.updateBusinessStatus, id, { status: newStatus });
  if (status === 200) {
    yield put(updateBusinessStatusSuccess());
    yield put(showAlert('Business status updated', { variant: 'success' }));
    // Refresh the businesses list
    yield put(fetchBusinessesAction());
  } else {
    yield put(showAlert(errMsg(data, 'Failed to update status'), { variant: 'danger' }));
    yield put(updateBusinessStatusFailure());
  }
}

export function* getBusinessMeRequest(api) {
  const { status, data } = yield call(api.getBusinessMe);
  if (status === 200) {
    yield put(getBusinessMeSuccess(data.data));
  } else {
    yield put(showAlert(errMsg(data, 'Failed to load profile'), { variant: 'danger' }));
    yield put(getBusinessMeFailure());
  }
}

export function* getBusinessRewardsRequest(api) {
  const { status, data } = yield call(api.getBusinessRewards);
  if (status === 200) {
    yield put(getBusinessRewardsSuccess(data.data));
  } else {
    yield put(showAlert(errMsg(data, 'Failed to load rewards'), { variant: 'danger' }));
    yield put(getBusinessRewardsFailure());
  }
}

export function* uploadBusinessRewardsRequest(api, action) {
  const { file } = action.payload;
  const formData = new FormData();
  formData.append('file', file);

  const { status, data } = yield call(api.uploadBusinessRewards, formData);
  if (status === 201) {
    yield put(uploadBusinessRewardsSuccess(data.data));
    yield put(
      showAlert(`Rewards uploaded successfully (${data.data?.created ?? 0} created)`, {
        variant: 'success',
      }),
    );
    // Refresh the rewards list
    yield put(fetchRewardsAction());
  } else {
    yield put(showAlert(errMsg(data, 'Upload failed'), { variant: 'danger' }));
    yield put(uploadBusinessRewardsFailure());
  }
}

export function* deleteBusinessRewardRequest(api, action) {
  const { rewardId } = action.payload;
  const { status, data } = yield call(api.deleteBusinessReward, rewardId);
  if (status === 200) {
    yield put(deleteBusinessRewardSuccess(rewardId));
    yield put(showAlert('Reward removed', { variant: 'success' }));
  } else {
    yield put(showAlert(errMsg(data, 'Failed to delete reward'), { variant: 'danger' }));
    yield put(deleteBusinessRewardFailure());
  }
}

export function* getBusinessRedemptionsRequest(api) {
  const { status, data } = yield call(api.getBusinessRedemptions);
  if (status === 200) {
    yield put(getBusinessRedemptionsSuccess(data.data));
  } else {
    yield put(showAlert(errMsg(data, 'Failed to load redemptions'), { variant: 'danger' }));
    yield put(getBusinessRedemptionsFailure());
  }
}

export function* getBusinessQrRequest(api, action) {
  const { businessId } = action.payload;
  const { status, data } = yield call(api.getBusinessQr, businessId);
  if (status === 200) {
    yield put(getBusinessQrSuccess(data.data));
  } else {
    yield put(showAlert(errMsg(data, 'Failed to load QR code'), { variant: 'danger' }));
    yield put(getBusinessQrFailure());
  }
}
